import React, { useCallback, useEffect } from 'react';
import { useAbility } from '@casl/react';
import { cloneDeep, map } from 'lodash';

import { Stepper } from '@/components/generic/Stepper';
import { HeaderStep, StepChangeParams } from '@/components/generic/Stepper/types';
import { AbilityContext } from '@/hooks/ability/Can';

import { initialWizardHeaderSteps } from '../../constants';
import { useOrderContext, useOrderDispatch } from '../../helpers';
import { StepsName } from '../../types';
import { StepWrapper } from '../Common/StepWrapper';
import { ConfirmationStep } from '../ConfirmationStep';
import { EligibilityStep } from '../EligibilityStep';
import OrderDetailsStep from '../OrderDetailsStep';
import { OrderInfoStep } from '../OrderInfoStep';
import { ShippingStep } from '../ShippingStep';
import { SuccessStep } from '../SuccessStep';

import { Props } from './types';

import modalStyles from '../../SubmitOrderModal.module.scss';

export const Steps = ({
  shouldShowEligibilityStep,
  titleRef,
  closeSubmitOrderModal,
  openCancellationModal,
}: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const ability = useAbility(AbilityContext);
  const canSeeEligibilityStep =
    ability.can('view', 'CHECKOUT_ACKNOWLEDGEMENT') && shouldShowEligibilityStep;

  const onStepChange = useCallback(
    ({ activeStep }: StepChangeParams): void => {
      if (titleRef?.current) {
        titleRef.current.scrollIntoView();
      }

      const isShippingStepSkipped = (headerStep: HeaderStep) =>
        headerStep.isHidden && headerStep.step === 2;
      const isCurrentStep = (step: number) => step === activeStep;

      const headerSteps = map(state.wizardHeaderSteps, headerStep => ({
        ...headerStep,
        isActive: isShippingStepSkipped(headerStep) ?? isCurrentStep(headerStep.step),
      }));

      dispatch({
        type: 'UPDATE',
        payload: {
          wizardHeaderSteps: headerSteps,
        },
      });
    },
    [dispatch, state.wizardHeaderSteps, titleRef],
  );

  useEffect(() => {
    let steps = cloneDeep(state.wizardHeaderSteps);

    if (state.isAccessProgram) {
      if (state.isDistribution) {
        steps[0].label = StepsName['Contract Details'];
        steps[1].label = StepsName['Services'];
        steps[2].label = StepsName['Request Contract'];
      }

      if (!state.isDistribution) {
        steps[0].label = StepsName['Products'];
        steps[2].label = StepsName['Request Contract'];

        const orderInfoStep = {
          step: 0,
          label: StepsName['Order Details'],
        };

        steps = [orderInfoStep, ...steps];
      }
    }

    if (!state.isAccessProgram) {
      if (state.isDistribution) {
        steps = [
          {
            step: 1,
            label: StepsName['Order Details'],
          },
          {
            step: 2,
            label: StepsName.Confirmation,
          },
          {
            step: 3,
            label: StepsName.Success,
            isHidden: true,
            isActive: false,
          },
        ];
      }
    }

    if (canSeeEligibilityStep) {
      const eligibilityStep = {
        step: 0,
        label: StepsName['Eligibility'],
      };

      steps = [eligibilityStep, ...steps];
    }

    steps = steps.map((item, index) => ({
      ...item,
      step: index + 1,
      isActive: false,
    }));

    steps[0].isActive = true;

    dispatch({
      type: 'UPDATE',
      payload: {
        wizardHeaderSteps: steps,
      },
    });

    // We return a cleanup function to restore the initial steps of the wizard
    // so if the useEffect is re-run, we don't add duplicated steps
    return (): void => {
      dispatch({
        type: 'UPDATE',
        payload: {
          wizardHeaderSteps: [...initialWizardHeaderSteps],
        },
      });
    };
  }, [canSeeEligibilityStep, dispatch, state.isAccessProgram]);

  const getStepByLabel = (label: StepsName) => {
    switch (label) {
      case StepsName['Eligibility']:
        return (
          <EligibilityStep close={openCancellationModal} stepName={StepsName['Eligibility']} />
        );

      case StepsName['Contract Details']:
        return (
          <OrderInfoStep close={openCancellationModal} stepName={StepsName['Contract Details']} />
        );

      case StepsName['Order Details']:
        return state.isAccessProgram ? (
          <OrderInfoStep close={openCancellationModal} stepName={StepsName['Order Details']} />
        ) : (
          <OrderDetailsStep close={openCancellationModal} stepName={StepsName['Order Details']} />
        );

      case StepsName['Products']:
        return <OrderDetailsStep close={openCancellationModal} stepName={StepsName['Products']} />;

      case StepsName['Services']:
        return <OrderDetailsStep close={openCancellationModal} stepName={StepsName['Services']} />;

      case StepsName['Shipping']:
        return <ShippingStep close={openCancellationModal} stepName={StepsName['Shipping']} />;

      case StepsName['Confirmation']:
      case StepsName['Request Contract']:
        return <ConfirmationStep close={openCancellationModal} stepName={label} />;

      case StepsName['Success']:
        return <SuccessStep close={closeSubmitOrderModal} />;

      default:
        return null;
    }
  };

  const hasProductStep = state.wizardHeaderSteps.find(step => step.label === StepsName['Products']);

  if (state.isAccessProgram && !state.isDistribution && !hasProductStep) {
    return null;
  }

  return (
    <Stepper
      classes={{ header: modalStyles.stepper_header }}
      headerSteps={state.wizardHeaderSteps}
      initialStep={1}
      onStepChange={onStepChange}
    >
      {state.wizardHeaderSteps.map(({ label, step }) => (
        <StepWrapper key={`${label}-${step}`} stepName={label} stepNumber={step}>
          {getStepByLabel(label as StepsName)}
        </StepWrapper>
      ))}
    </Stepper>
  );
};
